/**
 * providers/ninetailed/use-ninetailed-preview-catalog.ts
 *
 * Fetches the full catalog of Ninetailed experiences + audiences the preview
 * widget needs to populate its editor panels — PREVIEW MODE ONLY.
 *
 * Why a client fetch: the `NinetailedPreviewPlugin` renders its Experiences and
 * Audiences panels from the arrays it is constructed with (it does not discover
 * them from the API), so those arrays must be known before the plugin is built.
 * The app otherwise fetches only the CURRENT page's experiences server-side; the
 * widget needs the WHOLE catalog. We fetch it here through the same-origin
 * `/api/graphql?preview=true` proxy (preview token stays server-side, draft
 * entries included).
 *
 * In non-preview mode this does NO network work and returns an empty, already
 * "loaded" catalog, so the production render path is completely unaffected.
 * Failures fall back to an empty catalog (never throws) so preview can never
 * break the page — the panels just stay empty, exactly as before.
 */

'use client';

import { useEffect, useState } from 'react';
import type { ExperienceConfiguration } from '@ninetailed/experience.js-shared';
import type { ExposedAudienceDefinition } from '@ninetailed/experience.js-preview-bridge';

import { getBrowserClient } from '@/lib/apolloClient';
import { NINETAILED_CATALOG_QUERY } from '@/lib/queries/ninetailedCatalogQuery';
import { mapExperiences } from '@/lib/ninetailed/mapExperiences';
import { mapAudiences } from '@/lib/ninetailed/mapAudiences';
import {
  mapEntryIds,
  type NinetailedEntryIdMaps,
} from '@/lib/ninetailed/mapEntryIds';

export interface NinetailedPreviewCatalog {
  experiences: ExperienceConfiguration[];
  audiences: ExposedAudienceDefinition[];
  /**
   * `nt id -> Contentful entry sys.id` lookups powering the widget's "Go to
   * editor" deep-links (the plugin callbacks only receive the Ninetailed ids).
   */
  entryIds: NinetailedEntryIdMaps;
  /**
   * True once the catalog is settled (immediately in non-preview; after the
   * fetch resolves/fails in preview). The provider folds this into the SDK mount
   * key so the preview plugin is (re)built with the populated catalog exactly once.
   */
  loaded: boolean;
}

const EMPTY_ENTRY_IDS: NinetailedEntryIdMaps = {
  experienceEntryIdByNtId: {},
  audienceEntryIdByNtId: {},
};

const EMPTY_LOADED: NinetailedPreviewCatalog = {
  experiences: [],
  audiences: [],
  entryIds: EMPTY_ENTRY_IDS,
  loaded: true,
};

export function useNinetailedPreviewCatalog(
  previewMode: boolean,
): NinetailedPreviewCatalog {
  const [catalog, setCatalog] = useState<NinetailedPreviewCatalog>(
    previewMode
      ? {
        experiences: [],
        audiences: [],
        entryIds: EMPTY_ENTRY_IDS,
        loaded: false,
      }
      : EMPTY_LOADED,
  );

  useEffect(() => {
    if (!previewMode) {
      setCatalog(EMPTY_LOADED);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        // errorPolicy 'all': Contentful returns partial `data` alongside
        // non-fatal errors when an experience links to a deleted/unpublished
        // audience (UNRESOLVABLE_LINK). The default 'none' would throw and drop
        // ALL data over one dangling link, blanking the whole widget. With 'all'
        // we keep every resolvable experience/audience; the defensive mappers
        // already skip the malformed/null entries.
        const { data } = await getBrowserClient(true).query({
          query: NINETAILED_CATALOG_QUERY,
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        });
        if (cancelled) return;
        const experienceCollection = (
          data as { ntExperienceCollection?: unknown }
        )?.ntExperienceCollection;
        const audienceCollection = (data as { ntAudienceCollection?: unknown })
          ?.ntAudienceCollection;
        setCatalog({
          experiences: mapExperiences(experienceCollection),
          audiences: mapAudiences(audienceCollection),
          entryIds: mapEntryIds(experienceCollection, audienceCollection),
          loaded: true,
        });
      } catch {
        if (!cancelled) setCatalog(EMPTY_LOADED);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [previewMode]);

  return catalog;
}
